import { Grid, IconButton, ListSubheader, Menu, TextField } from '@mui/material';
import * as React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import useDebounce from '../../../hooks/useDebounce';
import { useUserTags } from '../../../sic-lib/api/hooks/useUserTags';
import { genericReducer } from '../../../sic-lib/reducers/genericReducer';
import { Contact } from '../../../sic-lib/types/Contact';
import { IErrorFormValidation } from '../../../sic-lib/utils/formValidationHelpers';
import { groupTagsByAssignment } from '../../../sic-lib/utils/functionalReducers';
import MatchedTagsList from './MatchedTagList';

interface Props {
    contact: Contact;
    anchorEl: HTMLElement | null;
    menuId: string;
    onCloseHandler: () => void;
}

export default function MenuAddContactTagsRaw({
    contact,
    anchorEl,
    menuId,
    onCloseHandler,
}: Props) {
    const { classes } = useStyles();

    const { data: tagData } = useUserTags();

    const userTags = React.useMemo(() => {
        return tagData || [];
    }, [tagData]);

    const [compState, compDispatch] = React.useReducer<React.Reducer<CompState, any>>(
        genericReducer,
        {
            searchTagName: '',
            formError: null,
        },
    );

    const debouncedSearchedTag = useDebounce(compState.searchTagName, 500);

    const filteredTagList = React.useMemo(
        () => groupTagsByAssignment(userTags, contact.tags, debouncedSearchedTag),
        [userTags, contact.tags, debouncedSearchedTag],
    );

    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        compDispatch({ searchTagName: event.target.value });
    };

    return (
        <Grid container>
            <Menu id={menuId} anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onCloseHandler}>
                {/* DO NOT remove onKeyDown Prop, otherwise will give you a weird bug when type some key */}
                <Grid mr={2} minWidth={400} onKeyDown={e => e.stopPropagation()}>
                    <Grid container item direction="row" justifyContent="space-between">
                        <ListSubheader>Add Contact Tag</ListSubheader>
                        <IconButton aria-label="close" onClick={onCloseHandler} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                    <TextField
                        name="searchTagName"
                        value={compState.searchTagName}
                        label="Search or Create Tag"
                        onChange={handleChange}
                        className={classes.txtInput}
                        error={!!compState.formError}
                        fullWidth
                        helperText={compState.formError?.message}
                    />
                    <MatchedTagsList
                        tagList={filteredTagList}
                        contactId={contact.contactId}
                        userTags={userTags}
                        compDispatch={compDispatch}
                        tagName={compState.searchTagName}
                    />
                </Grid>
            </Menu>
        </Grid>
    );
}

interface CompState {
    searchTagName: string;
    formError: IErrorFormValidation | null;
}

const useStyles = makeStyles()((theme: Theme) => ({
    txtInput: {
        margin: theme.spacing(1),
    },
}));
